"use client";

import { createContext, useContext, useState } from "react";
import {
    addItemToCompareAction,
    removeItemFromCompareAction,
    getProductCompareAction,
    getProductSpecificCompareAction,
} from "@/actions/product.compare";
import { ProductCompareType } from "@/util/query/product-compare-query";
import toast from "react-hot-toast";
import { t } from "i18next";

interface ProductCompareContextData {
    productList: string[];
    productCompareList: ProductCompareType[];
    isUpating: boolean;
    setProductCompare: (data: any) => void;
    getProductCompare: () => Promise<any>;
    addItemToCompare: (item: any) => Promise<void>;
    removeItemFromCompare: (item: any) => Promise<void>;
    getProductSpecificCompare: () => Promise<void>;
    maximumCompareItemsReached: boolean;
    productSpecificData: string[];
    isProductEmpty: boolean;
}

const ProductCompareContext = createContext<ProductCompareContextData>({
    productList: [],
    productCompareList: [],
    isUpating: false,
    setProductCompare: () => {},
    getProductCompare: async () => {},
    addItemToCompare: async () => {},
    removeItemFromCompare: async () => {},
    maximumCompareItemsReached: false,
    productSpecificData: [],
    getProductSpecificCompare: async () => {},
    isProductEmpty: false,
});

export type addItemToCompareType = {
    msProductId: string;
}

export const ProductCompareProvider = ({ children }: { children: React.ReactNode }) => {
    const [isUpating, setIsUpating] = useState(false);
    const [productList, setProductList] = useState<string[]>([]);
    const [productCompareList, setProductCompareList] = useState<ProductCompareType[]>([]);
    const [maximumCompareItemsReached, setMaximumCompareItemsReached] = useState(false);
    const [productSpecificData, setProductSpecificData] = useState<any>(null);
    const [isProductEmpty, setIsProductEmpty] = useState(false);

    const setProductCompare = (data: any) => {
        // TODO: Implement logic to set product compare data
    };

    const getProductCompare = async () => {
        try {
            setIsUpating(true);
            const result = await getProductCompareAction();
            if (result) {
                setProductCompareList(result as ProductCompareType[]);
                setProductList(result.map(item => item.msProductId));
                setIsProductEmpty(result.length === 0);
                setMaximumCompareItemsReached(result.length >= 4); // Assuming max 4 items can be compared
            }
            setIsUpating(false);
        } catch (error) {
            setProductCompareList([]);
            setProductList([]);
            setIsProductEmpty(true);
            setMaximumCompareItemsReached(false); // Reset the maximum compare items flag
            setIsUpating(false);
        }
    };

    const addItemToCompare = async (params: addItemToCompareType) => {
        if (maximumCompareItemsReached) {
            toast.error(t("product_compare.maximum_reached"),{
                position: "bottom-center",
                duration: 3000,
            });
            return;
        }
        // Check if the product is already in the compare list
        if (productList.includes(params.msProductId)) {
            toast.error(t("product_compare.already_added"),{
                position: "bottom-center",
                duration: 3000,
            });
            return;
        }
        try {
            setIsUpating(true);
            const result = await addItemToCompareAction({
                msProductId: params.msProductId
            });
            if (result.success === false) {
                setIsUpating(false);
                toast.error(t("product_compare.added_failed"),{
                    position: "bottom-center",
                    duration: 3000,
                });
                return;
            }

            await getProductCompare(); // Refresh the product compare list after adding
            setIsUpating(false);
            if (result.success) {
                toast.success(t("product_compare.added_successfully"),{
                    position: "bottom-center",
                    duration: 3000,
                });
            }
        } catch (error) {
            setIsUpating(true);
            console.error("Error adding item to compare:", error);
            await getProductCompare(); // Ensure we refresh the list even if there's an error
            setIsUpating(false);
            toast.error(t("product_compare.added_failed"),{
                position: "bottom-center",
                duration: 3000,
            });
        }
    };

    const removeItemFromCompare = async (params: addItemToCompareType) => {
        try {
            await setIsUpating(true);
            const result = await removeItemFromCompareAction({
                msProductId: params.msProductId
            });
            if (result.success === false) {
                await setIsUpating(false);
                toast.error(t("product_compare.removed_failed"),{
                    position: "bottom-center",
                    duration: 3000,
                });
                return;
            }

            await getProductCompare(); // Refresh the product compare list after removing
            await setIsUpating(false);
            if (result.success) {
                toast.success(t("product_compare.removed_successfully"),{
                    position: "bottom-center",
                    duration: 3000,
                });
            }
        } catch (error) {
            await setIsUpating(true);
            console.error("Error removing item from compare:", error);
            await getProductCompare(); // Ensure we refresh the list even if there's an error
            await setIsUpating(false);
            toast.error(t("product_compare.removed_failed"),{
                position: "bottom-center",
                duration: 3000,
            });
        }
    };

    const getProductSpecificCompare = async () => {
        try {            
            setIsUpating(true);
            const result = await getProductSpecificCompareAction(productList);
            if (result) {
                setProductSpecificData(result);
            }
            setIsUpating(false);
        } catch (error) {
            console.error("Error fetching specific product compare:", error);
            setIsUpating(false);
        }
    };

    return (
        <ProductCompareContext.Provider value={{
            productList,
            productCompareList,
            isUpating,
            setProductCompare,
            getProductCompare,
            addItemToCompare,
            removeItemFromCompare,
            getProductSpecificCompare,
            maximumCompareItemsReached, // Assuming max 4 items can be compared,
            productSpecificData,
            isProductEmpty,
        }}>
            {children}
        </ProductCompareContext.Provider>
    );
};

export const useProductCompare = () => {
    return useContext(ProductCompareContext);
}



